'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';

import useAnalytics from '@/hooks/useAnalytics';
import { AnalyticsEvent, CommonProps, genFormId, now, pagePath } from '@/lib/analytics/formAnalytics';

type Options<TFieldValues extends FieldValues = FieldValues> = {
    formName?: string;
    step?: number | string;
    ignoreFields?: string[];
    methods: UseFormReturn<TFieldValues>;
};

/**
 * Add analytics events to a react-hook-form
 * @param formName event name used in analytics, set to falsy value to opt out of tracking for a form
 * @param step when used in a multistep form, set the same name but different step
 * @param ignoreFields used to leave out certain fields from being send. e.g. passwords and emails
 * @param methods
 */
export function useFormAnalytics<TFieldValues extends FieldValues = FieldValues>({
    formName,
    step,
    ignoreFields = [],
    methods,
}: Options<TFieldValues>) {
    const rhf = methods;

    const formIdRef = useRef<string>(genFormId());
    const startedAtRef = useRef<number | null>(null);
    const sentStartRef = useRef(false);
    const sentFields = useRef<Set<string>>(new Set());
    const lastIsSubmittingRef = useRef(false);
    const submitTrackedRef = useRef(false);

    const pathname = usePathname();
    const searchParams = useSearchParams();

    const analytics = useAnalytics();
    const track = useCallback(
        (eventName: AnalyticsEvent, properties: CommonProps & Record<string, unknown>) => {
            if (!analytics) {
                console.error('No analytics provider attached, event details', eventName, properties);
                return;
            }
            void analytics.track(eventName, properties);
        },
        [analytics]
    );

    const locationKey = useMemo(() => {
        const search = searchParams?.toString();
        return search ? `${pathname}?${search}` : (pathname ?? '');
    }, [pathname, searchParams]);

    const shouldTrack = useMemo(() => {
        return !!formName; // effectively off without name
    }, [formName]);

    // not used, we dont track values at the moment, so all fields are handeld icually
    // const shouldIgnoreField = useCallback(
    //     (fieldName: string) => {
    //         const normalized = fieldName.toLowerCase();
    //
    //         if (normalized.includes('password')) {
    //             return true;
    //         }
    //
    //         return ignoreFields.some((ignored) => ignored === fieldName || ignored.toLowerCase() === normalized);
    //     },
    //     [ignoreFields]
    // );

    const common = useCallback(
        (extra?: Record<string, unknown>) => {
            const startedAt = startedAtRef.current;

            return {
                form_id: formIdRef.current,
                form_name: formName ?? 'unknown',
                page_path: pagePath(),
                ts: now(),
                ...(startedAt ? { elapsed_ms: now() - startedAt } : {}),
                ...(step !== undefined ? { step } : {}),
                ...(extra ?? {}),
            };
        },
        [formName, step]
    );

    const onFirstFocus = useCallback(() => {
        if (!shouldTrack) return;
        if (sentStartRef.current) return;

        sentStartRef.current = true;
        startedAtRef.current = now();
        track('Form Started', common());
    }, [common, shouldTrack, track]);

    // Field completed tracking
    useEffect(() => {
        if (!shouldTrack) return;

        const subscription = rhf.watch((values, { name }) => {
            if (!sentStartRef.current) {
                // form start not triggered yet, can happen when first field is auto focues
                sentStartRef.current = true;
                startedAtRef.current = now();
                track('Form Started', common());
            }

            if (!name) return;
            if (sentFields.current.has(name)) return;

            const value = getNested(values, name);
            const nonEmpty = value !== undefined && value !== null && String(value).trim() !== '';

            if (nonEmpty) {
                sentFields.current.add(name);
                track('Form Field Filled', common({ field: name }));
            }
        });

        return () => subscription.unsubscribe();
    }, [rhf, common, shouldTrack, track]);

    const trackStepCompleted = useCallback(
        (stepLabel?: number | string) => {
            if (!shouldTrack) return;

            track('Form Step Completed', common(stepLabel !== undefined ? { step: stepLabel } : undefined));
        },
        [common, shouldTrack, track]
    );

    const {
        formState: { errors, isSubmitting },
    } = rhf;

    const trackFormSubmitted = useCallback(
        (origin: 'complete' | 'unmount') => {
            const allKeys = errors ? Object.keys(errors) : [];
            const rootError: any = (errors as any)?.root;
            const fieldKeys = allKeys.filter((k) => k !== 'root');
            const hasFieldErrors = fieldKeys.length > 0;

            const originExtra =
                origin === 'unmount' ? { submission_end: 'unmount' as const } : { submission_end: 'complete' as const };

            if (rootError) {
                track(
                    'Form Submitted',
                    common({
                        valid_submission: false,
                        server_error: typeof rootError.message === 'string' ? rootError.message : String(rootError),
                        ...originExtra,
                    })
                );
                return;
            }

            if (hasFieldErrors) {
                track(
                    'Form Submitted',
                    common({
                        valid_submission: false,
                        error_fields: fieldKeys,
                        error_count: fieldKeys.length,
                        ...originExtra,
                    })
                );
                return;
            }

            // No errors → counts as successful submit
            track(
                'Form Submitted',
                common({
                    valid_submission: true,
                    ...originExtra,
                })
            );
        },
        [common, errors, track]
    );

    // by reacting this rev, then we can use trackFormSubmitted in the use effect unmount without reacting to errors which would trigger the unmount at undesired times
    const trackFormSubmittedRef = useRef(trackFormSubmitted);
    useEffect(() => {
        trackFormSubmittedRef.current = trackFormSubmitted;
    }, [trackFormSubmitted]);

    // Submit tracking (including server errors via errors.root)
    useEffect(() => {
        if (!shouldTrack) return;

        const wasSubmitting = lastIsSubmittingRef.current;

        // Start of a new submit: false -> true
        if (!wasSubmitting && isSubmitting) {
            submitTrackedRef.current = false;
            lastIsSubmittingRef.current = true;
            return;
        }

        // Guard: only proceed when we go from submitting -> not submitting
        if (!wasSubmitting || isSubmitting) {
            lastIsSubmittingRef.current = isSubmitting;
            return;
        }

        // End of submit: wasSubmitting === true && isSubmitting === false
        trackFormSubmitted('complete');
        submitTrackedRef.current = true;
        lastIsSubmittingRef.current = false;
    }, [isSubmitting, trackFormSubmitted, shouldTrack]);

    useEffect(() => {
        return () => {
            if (!shouldTrack) return;
            if (!trackFormSubmittedRef.current) return;

            // Only fire if a submit was in progress and we never tracked it
            if (lastIsSubmittingRef.current && !submitTrackedRef.current) {
                trackFormSubmittedRef.current('unmount');
                submitTrackedRef.current = true;
            }
        };
    }, [shouldTrack]);

    // Reset per route
    useEffect(() => {
        formIdRef.current = genFormId();
        startedAtRef.current = null;
        sentStartRef.current = false;
        lastIsSubmittingRef.current = false;
        submitTrackedRef.current = false;
        sentFields.current.clear();
    }, [locationKey]);

    return {
        formId: formIdRef.current,
        onFirstFocus,
        trackStepCompleted,
    };
}

function getNested(obj: any, path: string): any {
    return path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), obj);
}
